import React, { FocusEvent } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import DeviceIcon from '../../img/deviceIcons/icon_device';
import MaterialIcon from '../MaterialIcon';
import styles from './NavigationItem.module.scss';

export type Props = {
    path: string;
    title: string;
    onClick?: () => void;
    iconName?: string;
    navigationType: string;
    id?: string;
    onBlur?: (event: FocusEvent<HTMLElement>) => void;
    extraClass?: string;
    invertedColors?: boolean;
    centered?: boolean;
};

interface NavigationItemContentProps {
    iconName?: string;
    navigationType: string;
    title: string;
    txt: (text: string) => string;
    extraClass?: string;
}

const NavigationItemContent = ({
    iconName,
    navigationType,
    title,
    txt,
    extraClass,
}: NavigationItemContentProps): React.ReactElement => (
    <li className={styles.navItemTitle}>
        {iconName !== undefined &&
            (iconName === 'deviceIcon' ? <DeviceIcon /> : <MaterialIcon extraClass={extraClass} name={iconName} />)}
        <span
            className={classNames({
                [styles.onlyShowIcon]: navigationType === 'slim',
            })}
        >
            {txt(title)}
        </span>
    </li>
);

const NavigationItem = ({
    path,
    title,
    iconName,
    navigationType,
    id,
    onBlur,
    onClick,
    extraClass,
    invertedColors,
    centered,
}: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const { pathname, search } = useLocation();

    if (id === 'shop') {
        return (
            <a id={id} href={path} rel="noopener noreferrer" target="_blank" className={classNames(styles.item)}>
                <NavigationItemContent
                    title={title}
                    iconName={iconName}
                    navigationType={navigationType}
                    txt={txt}
                    extraClass={extraClass}
                />
            </a>
        );
    }
    const pathQuery = path.includes('?') && path.substring(path.indexOf('?'));
    const elementWithEmptyQuery = pathQuery && pathQuery.length === 9 ? path.substring(0, path.indexOf('?')) : path;
    const elementIsActive =
        path === pathname || path === `${pathname}${search}` || elementWithEmptyQuery === `${pathname}${search}`;
    return (
        <NavLink
            id={id}
            className={(): string =>
                classNames(styles.item, {
                    [styles.centered]: centered,
                    [styles.active]: elementIsActive && !invertedColors,
                    [styles.centeredActive]: elementIsActive && centered && !invertedColors,
                    [styles.activeDropdownItem]: elementIsActive && centered,
                    [styles.inverted]: invertedColors,
                    [styles.invertedActive]: invertedColors && elementIsActive,
                })
            }
            onBlur={onBlur}
            onClick={onClick}
            to={path}
        >
            <NavigationItemContent
                title={title}
                iconName={iconName}
                navigationType={navigationType}
                txt={txt}
                extraClass={extraClass}
            />
        </NavLink>
    );
};

export default NavigationItem;
